/* eslint-disable react/no-array-index-key */
/* eslint-disable indent */
import React, { useContext, useState } from 'react';
import {
  Table,
  TableBody,
  TableContainer,
  Paper,
  makeStyles,
} from '@material-ui/core';
import {
  MED_IS_CURRENT_VERSION_TRUE,
  RCOPIA_STOPREASON_DELETED,
  MED_IS_CURRENT_VERSION_FALSE,
} from '@mummssoftware/utils/constants';
import { parseDateMMDDYYYYSlashes } from '@mummssoftware/utils/dates';
import {
  ConfirmModal,
  DialogBox,
  NoRecordsDiv,
} from '@mummssoftware/common-ui';
import { isNumber, merge } from 'lodash-es';
import { MedContext } from '../App/MedContext';
import {
  PENDING_PRESCRIPTION,
  ACTIVE_KEY,
  PRESCRIPTIONS_KEY,
} from '../App/constants';
import { Medication, EpisodeExpansion } from '../Sections/Medication';
import { Headers } from '../Sections/Medication/ColHeaders';
import { messages } from '../utils/messages';
import defaults from '../../translations/en.json';
import '@mummssoftware/common-ui/style/css/ag-theme-hummingbird.css';

// todo use this again
// const SpaceDiv = styled.div`
//   height: 100px;
//   width: 100%;
// `;

const scrollBarWidth = 14;

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
  },
  paper: (props) => {
    const { smallWidgetHeight, getFullSizeHeight } = theme.props.dashboard;
    const { paper } = theme.palette.background;
    return props.dashboardEmbedded
      ? {
          background: paper,
          overflow: 'scroll',
          overscrollBehavior: 'contain',
          height: props.smallWidget
            ? smallWidgetHeight
            : getFullSizeHeight(window.innerHeight) - props.actionBarHeight,
          width: '100%',
          borderRadius: 0,
        }
      : {
          background: paper,
          overflow: 'visible',
          width: '100%',
          marginBottom: theme.spacing(2),
          borderRadius: 0,
        };
  },
  table: (props) =>
    props.dashboardEmbedded
      ? {
          minWidth: props.containerWidth - scrollBarWidth,
          width: '100%',
        }
      : { minWidth: 750 },
  tableContainer: (props) =>
    props.dashboardEmbedded
      ? {
          minHeight: '200px',
          background: theme.palette.background.paper,
        }
      : {
          background: theme.palette.background.paper,
          overflow: 'visible',
        },
  visuallyHidden: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    top: 20,
    width: 1,
  },
}));

function descendingComparator(a, b, orderBy) {
  // need to sort by alpha, date, number, and bool
  if (orderBy === 'name') {
    const orderByName = 'drugdescription';
    if (b[orderByName].toLowerCase() < a[orderByName].toLowerCase()) {
      return -1;
    }
    if (b[orderByName].toLowerCase() > a[orderByName].toLowerCase()) {
      return 1;
    }
  }
  if (orderBy === 'sig' || orderBy === 'physician') {
    if (!a[orderBy]) return 1;
    if (!b[orderBy]) return -1;

    if (b[orderBy].toLowerCase() < a[orderBy].toLowerCase()) {
      return -1;
    }
    if (b[orderBy].toLowerCase() > a[orderBy].toLowerCase()) {
      return 1;
    }
  }
  if (orderBy === 'covered') {
    if (b.coveredbyhospice.toLowerCase() < a.coveredbyhospice.toLowerCase()) {
      return -1;
    }
    if (b.coveredbyhospice.toLowerCase() > a.coveredbyhospice.toLowerCase()) {
      return 1;
    }
  }

  if (orderBy === 'quantity') {
    if (Number(b[orderBy]) < Number(a[orderBy])) {
      return -1;
    }
    if (Number(b[orderBy]) > Number(a[orderBy])) {
      return 1;
    }
  }

  if (orderBy === 'start') {
    const aDate = a.startdate_shifted
      ? parseDateMMDDYYYYSlashes(a.startdate_shifted)
      : false;
    const bDate = b.startdate_shifted
      ? parseDateMMDDYYYYSlashes(b.startdate_shifted)
      : false;
    if (bDate < aDate) {
      return -1;
    }
    if (bDate > aDate) {
      return 1;
    }
  }
  if (orderBy === 'stop' || orderBy === 'approved') {
    const aDate = a.stopdate_shifted
      ? parseDateMMDDYYYYSlashes(a.stopdate_shifted)
      : false;
    const bDate = b.stopdate_shifted
      ? parseDateMMDDYYYYSlashes(b.stopdate_shifted)
      : false;
    if (bDate < aDate) {
      return -1;
    }
    if (bDate > aDate) {
      return 1;
    }
  }

  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const Medications = ({
  expand,
  doctors,
  hospicePhys,
  patient,
  handleMedChange,
  showCheckmarks,
  updateApprovedMedsHash,
  ...props
}) => {
  // adds more value for phy dropdown in medication
  const hospicePhysWithMore = hospicePhys?.concat([
    { value: 'more', label: 'more...' },
  ]);
  const { medications, dimensions } = useContext(MedContext);
  const actionBarHeight = props.smallWidget ? undefined : dimensions;
  const newprops = props.smallWidget ? props : { ...props, actionBarHeight };
  const classes = useStyles(newprops);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [episodeSyncAcknowledged, setEpisodeSyncAcknowledged] = useState(false);

  const { episodes } = patient;
  const patientEpisodes =
    episodes &&
    [...episodes].reduce((seed, e) => {
      const { episodeNumber } = e;
      if (isNumber(episodeNumber)) {
        const title = {
          utcAdmitDate: e.utcAdmitDate,
          utcDischargeDate: e.utcDischargeDate,
          utcReferralDate: e.utcReferralDate,
          admitDate: e.admitDate,
          dischargeDate: e.dischargeDate,
          referralDate: e.referralDate,
          episodeNumber,
        };
        seed[episodeNumber] = { title };
      }

      return seed;
    }, {});

  // Med episodenum must match a patient episodeNumber. null/empty is invalid.
  // episodenum "0" is valid only when the patient has episodeNumber 0.
  const patientEpisodeKeys = new Set(
    Object.keys(patientEpisodes || {}).map(String),
  );
  const hasInvalidEpisodeMeds = [...medications].some(({ episodenum }) => {
    if (episodenum == null || episodenum === '') {
      return true;
    }
    return !patientEpisodeKeys.has(String(episodenum));
  });
  const showEpisodeSyncWarning =
    hasInvalidEpisodeMeds && !episodeSyncAcknowledged;

  const medicationsByEpisode = [...medications].reduce((seed, m) => {
    const { episodenum } = m;
    if (episodenum && episodenum !== '') {
      if (seed[episodenum]) {
        seed[episodenum].meds.push(m);
      } else {
        seed[episodenum] = {};
        seed[episodenum].meds = [m];
      }
    }
    // if there is no episodenum then all go to episodenum = 0
    if (!episodenum || episodenum === '') {
      if (seed[0]) {
        seed[0].meds.push(m);
      } else {
        seed[0] = {};
        seed[0].meds = [m];
      }
    }

    return seed;
  }, {});

  const allEpisodes = merge(medicationsByEpisode, patientEpisodes);
  // Meds may reference an episodenum with no matching patient.episodes entry
  // (or none at all → bucket 0). Those buckets have meds but no title — guard
  // so EpisodeExpansion does not throw on title.referralDate.
  const allEpisodesbyTitle = Object.entries(allEpisodes)
    .filter(([, e]) => e.meds)
    .map(([episodeKey, e]) => ({
      ...e,
      title:
        e.title ||
        (episodeKey === '0' ? 'Other medications' : `Episode ${episodeKey}`),
    }));
  const allEpisodesOrdered = allEpisodesbyTitle.reverse();
  const [ready, setReady] = useState(false);

  const newEpisodes = [...allEpisodesOrdered].map((sequence) => {
    const { meds } = sequence;
    const newMedications = [...meds];
    const noParentMeds = [];
    // list of history meds
    const historyMeds = [...meds].filter(
      (m) => m.iscurrentversion === MED_IS_CURRENT_VERSION_FALSE,
    );
    // tag the med with medindex === 0 with history === yes if there are other meds in the
    // episode with the same externalid as this med.
    historyMeds.forEach((med) => {
      const index = newMedications.findIndex(
        ({ externalid, iscurrentversion }) =>
          iscurrentversion === MED_IS_CURRENT_VERSION_TRUE &&
          externalid === med.externalid,
      );
      if (index > -1) {
        const newMed = {
          ...newMedications[index],
          history: 'yes',
        };
        newMedications[index] = newMed;
        // if parent not in current episode we still need to display those meds in the
        // current episode, push to array
      } else {
        noParentMeds.push(med);
      }
    });
    // the meds to display are the one with iscurrentversion == yes and the one with
    // iscurrentversion == no and no parent id
    const currentMeds = [...newMedications].filter(
      (m) => m.iscurrentversion === MED_IS_CURRENT_VERSION_TRUE,
    );
    const episodeMeds = [...currentMeds, ...noParentMeds];

    const deleted = [...episodeMeds].filter(
      (m) =>
        m.stopdate_shifted &&
        m.stopreason === RCOPIA_STOPREASON_DELETED &&
        !m.isprescription,
    );

    const today = new Date();
    const stopped = [...episodeMeds].filter((med) => {
      const isNotDeleted = med.stopreason !== RCOPIA_STOPREASON_DELETED;
      const isStopped =
        med.iscurrentversion === MED_IS_CURRENT_VERSION_FALSE ||
        (med.stopdate_shifted &&
          today > parseDateMMDDYYYYSlashes(med.stopdate_shifted));
      return isNotDeleted && isStopped;
    });

    const activeMedHash = {};
    let active = [...episodeMeds].filter((m) => {
      const isActive =
        !m.isprescription &&
        m.iscurrentversion &&
        m.iscurrentversion === MED_IS_CURRENT_VERSION_TRUE &&
        (today < parseDateMMDDYYYYSlashes(m.stopdate_shifted) ||
          m.stopdate_shifted === '');
      if (isActive && m.prescriptionrcopiaid && m.prescriptionrcopiaid !== '') {
        activeMedHash[m.prescriptionrcopiaid] = m;
      }
      return isActive;
    });

    // Bug in Rcopia is sending back stop reasons on restarted medications that where
    // previously stopped.  Adding a flag so that we can hide active meds stopreasons
    // in the Med rows for active meds.
    active = active.map((m) => {
      const updatedMed = { ...m };
      updatedMed.hideStopReason = !!m.stopreason;
      return updatedMed;
    });
    // only show prescription records that do not have an active copy of the medication
    const prescriptions = [...meds].filter(
      (m) => m.isprescription && !Object.hasOwn(activeMedHash, m.externalid),
    );

    const newMeds = {};
    if (prescriptions.length) {
      newMeds.prescriptions = prescriptions;
    }
    if (active.length) {
      newMeds.active = active;
    }
    if (stopped.length) {
      newMeds.stopped = stopped;
    }
    if (deleted.length) {
      newMeds.deleted = deleted;
    }

    const newSequence = sequence;
    newSequence.meds = newMeds;
    !ready && setReady(true);

    return newSequence;
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const NoMeds = () => (
    <NoRecordsDiv>
      <p>{defaults.noMeds}</p>
    </NoRecordsDiv>
  );

  const shouldBeExpanded = (key) =>
    key === ACTIVE_KEY || (key === PRESCRIPTIONS_KEY && !props.smallWidget);

  return ready ? (
    <>
      <DialogBox
        open={showEpisodeSyncWarning}
        noIcon
        disableBackdropClick
        disableEscapeKeyDown
        handleClick={() => setEpisodeSyncAcknowledged(true)}
        title={defaults.listing.medsConfDialogTitle}
      >
        <ConfirmModal
          hideCancel
          onOKClick={() => setEpisodeSyncAcknowledged(true)}
          okText={defaults.listing.okBTN}
          modalText={defaults.listing.medsEpisodeOutOfSync}
        />
      </DialogBox>
      <Paper className={classes.paper}>
        {newEpisodes.length ? (
          <React.Fragment>
            {newEpisodes.map((e, i) => {
              const { title, meds } = e;
              const episodeKey =
                (title && (title.referralDate || title.episodeNumber)) ||
                `episode-${i}`;
              return (
                <EpisodeExpansion
                  title={title}
                  smallWidget={props.smallWidget}
                  isExpanded={i === 0}
                  key={episodeKey}
                >
                  {Object.keys(meds).map((key, ind) => (
                    <EpisodeExpansion
                      title={`${
                        key === PRESCRIPTIONS_KEY ? PENDING_PRESCRIPTION : key
                      } (${meds[key].length})`.toUpperCase()}
                      smallWidget={props.smallWidget}
                      isExpanded={() => shouldBeExpanded(key)}
                      key={`${key}-${ind}`}
                      small
                    >
                      <TableContainer
                        className={`ag-theme-hummingbird light ${classes.tableContainer}`}
                      >
                        <Table
                          stickyHeader={!!props.dashboardEmbedded}
                          className={classes.table}
                          aria-labelledby="tableTitle"
                          aria-label="medications table"
                          padding="none"
                        >
                          <Headers
                            classes={classes}
                            messages={messages}
                            patient={patient}
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            hide={ind !== 0}
                            medications={meds[key]}
                            showStartReasonColumn={
                              key === ACTIVE_KEY || key === PRESCRIPTIONS_KEY
                            }
                            // rowCount={medications.length}
                          />
                          <TableBody>
                            {stableSort(
                              meds[key],
                              getComparator(order, orderBy),
                            ).map((medication, index) => (
                              <Medication
                                className={index % 2 ? 'ag-row-odd' : ''}
                                key={medication.id}
                                expand={expand}
                                doctors={doctors}
                                hospicePhys={hospicePhysWithMore}
                                medication={medication}
                                handleChange={handleMedChange}
                                medications={medications}
                                containerWidth={props.containerWidth}
                                showCheckmarks={showCheckmarks}
                                updateApprovedMedsHash={updateApprovedMedsHash}
                                patient={patient}
                                showStartReasonColumn={
                                  key === ACTIVE_KEY ||
                                  key === PRESCRIPTIONS_KEY
                                }
                              />
                            ))}
                          </TableBody>
                        </Table>
                        {/* {!props.smallWidget && <SpaceDiv />} */}
                      </TableContainer>
                    </EpisodeExpansion>
                  ))}
                </EpisodeExpansion>
              );
            })}
          </React.Fragment>
        ) : (
          <NoMeds />
        )}
      </Paper>
    </>
  ) : (
    <Paper className={classes.paper}></Paper>
  );
};

export default React.memo(Medications);
